import { addDays, differenceInCalendarDays, format, startOfDay, subDays } from 'date-fns';
/**
 * Horizon de publication des tarifs aériens : au-delà de J+358, aucun tarif
 * n'existe encore. Borne inclusive — J+358 pile est le dernier jour chiffrable.
 * Ex. le 27/07/2026 → dernier jour sélectionnable le 20/07/2027.
 */
export const TRAVEL_HORIZON_DAYS = 358;
/** Dernier jour de voyage chiffrable (aujourd'hui + 358 j, inclus). */
export const getMaxTravelDate = (now = new Date()) => addDays(startOfDay(now), TRAVEL_HORIZON_DAYS);
/** Hors fenêtre chiffrable : jour passé ou au-delà de J+358. */
export const isOutsideTravelWindow = (date, now = new Date()) => date < startOfDay(now) || date > getMaxTravelDate(now);
/** Message unique, servi au calendrier, au schéma zod et au 400 serveur. */
export const travelHorizonMessage = (now = new Date()) => `Nous n'établissons pas de devis au-delà du ${format(getMaxTravelDate(now), 'dd/MM/yyyy')} : les tarifs ne sont pas encore publiés.`;
// ---------------------------------------------------------------------------
// « Pour plus tard » — éligibilité au rangement piloté par l'horizon tarifaire
// ---------------------------------------------------------------------------
/** Jours calendaires jusqu'à la date de retour du voyage. */
export const daysUntilTravelReturn = (returnDate, now = new Date()) => differenceInCalendarDays(returnDate, now);
/**
 * Éligible au rangement « Pour plus tard » ⟺ retour STRICTEMENT au-delà de
 * J+358. Strict, parce que la borne publique est inclusive (J+358 pile est
 * chiffrable et demandable par un client) : c'est le complément exact de la
 * fenêtre chiffrable, et le cron libère dès `<= 358`.
 */
export const isEligibleForTariffSnooze = (returnDate, now = new Date()) => daysUntilTravelReturn(returnDate, now) > TRAVEL_HORIZON_DAYS;
/** Premier jour où les tarifs du voyage sont publiés : retour − 358 jours. */
export const tariffAvailabilityDate = (returnDate) => subDays(returnDate, TRAVEL_HORIZON_DAYS);
/**
 * Message unique du refus de rangement, servi au 400 serveur ET au tooltip du
 * bouton désactivé. Chaque nombre est accompagné de sa date (principe 2 de
 * PRODUCT.md — pas de date nue).
 */
export const tariffSnoozeRefusalMessage = (returnDate, now = new Date()) => {
    const days = daysUntilTravelReturn(returnDate, now);
    return (`Ce devis ne peut pas être rangé pour plus tard : les tarifs de ce voyage sont déjà disponibles. ` +
        `Retour le ${format(returnDate, 'dd/MM/yyyy')}, dans ${days} jour${days > 1 ? 's' : ''} — ` +
        `seuls les voyages dont le retour est à plus de ${TRAVEL_HORIZON_DAYS} jours peuvent y être placés.`);
};
const parseFormDate = (value) => {
    if (!value)
        return null;
    // Minuit UTC, même convention que les dates de voyage en base (parseDateOnly).
    const parsed = new Date(`${value.slice(0, 10)}T00:00:00.000Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};
/**
 * Date de retour dérivée de valeurs de formulaire : le tronçon le plus lointain
 * pour un vol (l'ordre de saisie ne compte pas, un ONE_WAY n'a qu'un tronçon),
 * la date de retour pour un séjour. `null` si indérivable — l'appelant décide
 * (fail-closed côté serveur).
 */
export function resolveTravelReturnDate(input) {
    if (input.estimateType === 'TOURISM')
        return parseFormDate(input.returnDate);
    const dates = (input.flights ?? [])
        .map((flight) => parseFormDate(flight.date))
        .filter((date) => date !== null);
    if (dates.length === 0)
        return null;
    let max = dates[0];
    for (const date of dates) {
        if (date > max) {
            max = date;
        }
    }
    return max;
}
